import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { resetPassword } from '../services/authService';
import { Logo } from './Logo';
import './ResetPassword.css';

export const ResetPassword: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const token = searchParams.get('token');

	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string>('');
	const [success, setSuccess] = useState(false);
	const [tokenValid, setTokenValid] = useState(true);

	useEffect(() => {
		if (!token) {
			setError('Nevažeći ili nedostajući token za resetovanje lozinke');
			setTokenValid(false);
		}
	}, [token]);

	const validatePassword = (password: string): string[] => {
		const errors: string[] = [];

		if (password.length < 6) {
			errors.push('Lozinka mora imati minimum 6 karaktera');
		}

		return errors;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!token) {
			setError('Nevažeći token za resetovanje lozinke');
			return;
		}

		if (!newPassword.trim()) {
			setError('Nova lozinka je obavezna');
			return;
		}

		// Validate password
		const passwordErrors = validatePassword(newPassword);
		if (passwordErrors.length > 0) {
			setError(passwordErrors[0]);
			return;
		}

		// Check if passwords match
		if (newPassword !== confirmPassword) {
			setError('Lozinke se ne poklapaju');
			return;
		}

		setError('');
		setIsLoading(true);

		try {
			await resetPassword(token, newPassword);
			setSuccess(true);

			// Redirect to login after 5 seconds
			setTimeout(() => {
				navigate('/');
			}, 5000);
		} catch (err) {
			console.error('Reset password error:', err);
			setError(err instanceof Error ? err.message : 'Greška pri resetovanju lozinke. Molimo pokušajte ponovo.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoToLogin = () => {
		navigate('/');
	};

	const handleInputChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
		if (field === 'newPassword') {
			setNewPassword(value);
		} else {
			setConfirmPassword(value);
		}
		if (error) setError('');
	};

	if (!tokenValid) {
		return (
			<div className='reset-password-container'>
				<div className='reset-password-card'>
					<div className='reset-password-header'>
						<Logo size='large' />
						<h1>Nevažeći link</h1>
					</div>
					<div className='reset-password-content'>
						<p className='error-text'>Link za resetovanje lozinke je nevažeći ili je istekao.</p>
						<button onClick={handleGoToLogin} className='btn btn-primary'>
							Nazad na prijavljivanje
						</button>
					</div>
				</div>
			</div>
		);
	}

	if (success) {
		return (
			<div className='reset-password-container'>
				<div className='reset-password-card'>
					<div className='reset-password-header'>
						<Logo size='large' />
						<h1>Lozinka je uspešno resetovana</h1>
					</div>
					<div className='reset-password-content'>
						<div className='success-message'>
							<p>Vaša lozinka je uspešno promenjena. Možete se sada prijaviti sa novom lozinkom.</p>
							<p>Bićete automatski preusmereni na stranicu za prijavljivanje za 5 sekundi...</p>
						</div>
						<button onClick={handleGoToLogin} className='btn btn-primary'>
							Prijavite se odmah
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='reset-password-container'>
			<div className='reset-password-card'>
				<div className='reset-password-header'>
					<Logo size='large' />
					<h1>Postavite novu lozinku</h1>
					<p>Unesite vašu novu lozinku</p>
				</div>

				<form onSubmit={handleSubmit} className='reset-password-form'>
					{error && <div className='error-message'>{error}</div>}

					<div className='form-group'>
						<label htmlFor='newPassword'>Nova lozinka *</label>
						<input type='password' id='newPassword' value={newPassword} onChange={(e) => handleInputChange('newPassword', e.target.value)} placeholder='Minimum 6 karaktera' required disabled={isLoading} />
					</div>

					<div className='form-group'>
						<label htmlFor='confirmPassword'>Potvrdite lozinku *</label>
						<input type='password' id='confirmPassword' value={confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} placeholder='Ponovite novu lozinku' required disabled={isLoading} />
					</div>

					<div className='password-requirements'>
						<p className='requirements-title'>Zahtevi za lozinku:</p>
						<ul className='requirements-list'>
							<li className={newPassword.length >= 6 ? 'valid' : ''}>Najmanje 6 karaktera</li>
						</ul>
					</div>

					<button type='submit' className='btn btn-primary reset-button' disabled={isLoading || !newPassword || !confirmPassword}>
						{isLoading ? 'Resetovanje...' : 'Resetuj lozinku'}
					</button>

					<button type='button' onClick={handleGoToLogin} className='btn btn-secondary back-to-login' disabled={isLoading}>
						Nazad na prijavljivanje
					</button>
				</form>
			</div>
		</div>
	);
};
